<script setup lang="ts">
import { onMounted, ref } from "vue";
import { BookOpenText, Bot, Check, Cpu, Plus, Save, ShieldCheck } from "lucide-vue-next";
import { api } from "../services/api";
import type { Agent, ClientApp, DeviceAction, KnowledgeBase } from "../types";

const props = defineProps<{ onAgentChange: () => Promise<void> | void }>();
const agents = ref<Agent[]>([]);
const clients = ref<ClientApp[]>([]);
const deviceActions = ref<DeviceAction[]>([]);
const knowledgeBases = ref<KnowledgeBase[]>([]);
const selectedId = ref("");
const draft = ref<Partial<Agent>>({});
const saving = ref(false);
const error = ref("");
const success = ref("");

function newAgent(): Partial<Agent> {
  return {
    name: "",
    slug: "",
    description: "",
    provider: "VOLCENGINE_ARK",
    textModel: "",
    instructions: "你是一个可靠、简洁的中文智能助手。",
    temperature: 0.2,
    maxOutputTokens: 4096,
    textEnabled: true,
    ttsEnabled: false,
    ttsSpeaker: "",
    realtimeEnabled: false,
    realtimeModel: "",
    realtimeSpeaker: "",
    realtimeBotName: "",
    realtimeSpeakingStyle: "自然、清晰、简洁",
    deviceControlEnabled: false,
    deviceControlAllowedFields: ["switch"],
    deviceActionIds: [],
    knowledgeEnabled: false,
    knowledgeNoMatchReply: "知识库中没有找到相关资料。",
    knowledgeFallbackToModel: true,
    knowledgeBaseIds: [],
    isEnabled: true,
  };
}

function allowedFieldsText(): string {
  return (draft.value.deviceControlAllowedFields ?? []).join(",");
}

function updateAllowedFields(value: string): void {
  draft.value.deviceControlAllowedFields = [...new Set(value.split(",").map((field) => field.trim()).filter(Boolean))];
}

async function load(): Promise<void> {
  error.value = "";
  try {
    [agents.value, clients.value, deviceActions.value, knowledgeBases.value] = await Promise.all([
      api.adminAgents(), api.clientApps(), api.adminDeviceActions(), api.adminKnowledgeBases(),
    ]);
    if (selectedId.value) {
      const selected = agents.value.find((agent) => agent.id === selectedId.value);
      if (selected) selectAgent(selected);
    } else if (agents.value[0]) selectAgent(agents.value[0]);
  } catch (reason) {
    error.value = reason instanceof Error ? reason.message : "管理数据加载失败";
  }
}

function selectAgent(agent: Agent): void {
  selectedId.value = agent.id;
  draft.value = {
    ...agent,
    deviceControlAllowedFields: [...agent.deviceControlAllowedFields],
    deviceActionIds: [...agent.deviceActionIds],
    knowledgeBaseIds: [...agent.knowledgeBaseIds],
  };
  success.value = "";
}

function startCreate(): void {
  selectedId.value = "";
  draft.value = newAgent();
  success.value = "";
}

async function save(): Promise<void> {
  if (!draft.value.name?.trim() || !draft.value.slug?.trim() || !draft.value.textModel?.trim() || !draft.value.instructions?.trim()) return;
  if (draft.value.deviceControlEnabled && !draft.value.deviceControlAllowedFields?.length && !draft.value.deviceActionIds?.length) {
    error.value = "启用设备工具时，至少填写一个允许字段或授权一个自定义动作";
    return;
  }
  if (draft.value.knowledgeEnabled && !draft.value.knowledgeBaseIds?.length) {
    error.value = "启用知识库时，至少关联一个知识库";
    return;
  }
  saving.value = true;
  error.value = "";
  success.value = "";
  try {
    const saved = await api.saveAgent(draft.value as Partial<Agent> & Pick<Agent, "name" | "slug">);
    await api.setAgentDeviceActions(saved.id, draft.value.deviceActionIds ?? []);
    await api.setAgentKnowledgeBases(saved.id, draft.value.knowledgeBaseIds ?? []);
    selectedId.value = saved.id;
    await load();
    success.value = "智能体配置已保存";
    await props.onAgentChange();
  } catch (reason) {
    error.value = reason instanceof Error ? reason.message : "保存失败";
  } finally {
    saving.value = false;
  }
}

async function toggleClientAccess(client: ClientApp, checked: boolean): Promise<void> {
  if (!selectedId.value) return;
  const next = checked ? [...new Set([...client.agentIds, selectedId.value])] : client.agentIds.filter((id) => id !== selectedId.value);
  try {
    await api.setClientAgents(client.id, next);
    client.agentIds = next;
    await props.onAgentChange();
  } catch (reason) {
    error.value = reason instanceof Error ? reason.message : "授权更新失败";
  }
}

onMounted(load);
</script>

<template>
  <div class="agent-layout">
    <aside class="agent-list surface-section">
      <div class="section-header">
        <div><span class="eyebrow">配置</span><h2>智能体</h2></div>
        <button class="icon-button compact" type="button" title="新建智能体" @click="startCreate"><Plus :size="17" /></button>
      </div>
      <button v-for="agent in agents" :key="agent.id" type="button" :class="['agent-list-item', { active: selectedId === agent.id }]" @click="selectAgent(agent)">
        <div class="agent-list-icon"><Bot :size="18" /></div>
        <div><strong>{{ agent.name }}</strong><span>{{ agent.slug }}</span></div>
        <span :class="['status-dot', agent.isEnabled ? 'ok' : 'bad']" />
      </button>
      <div v-if="!agents.length" class="empty-state">暂无智能体</div>
    </aside>

    <section class="surface-section agent-editor">
      <div class="section-header">
        <div><span class="eyebrow">{{ selectedId ? "编辑" : "新建" }}</span><h2>{{ draft.name || "智能体配置" }}</h2></div>
        <button class="button primary" type="button" :disabled="saving" @click="save"><Save :size="16" />{{ saving ? "保存中" : "保存" }}</button>
      </div>
      <div class="agent-form-grid">
        <label class="form-field"><span>名称</span><input v-model="draft.name" /></label>
        <label class="form-field"><span>标识</span><input v-model="draft.slug" :disabled="Boolean(selectedId)" /></label>
        <label class="form-field span-2"><span>说明</span><input v-model="draft.description" /></label>
        <label class="form-field span-2"><span>文字模型 / Endpoint</span><input v-model="draft.textModel" /></label>
        <label class="form-field span-2 tall"><span>系统提示词</span><textarea v-model="draft.instructions" /></label>
        <label class="form-field"><span>Temperature</span><input v-model.number="draft.temperature" type="number" min="0" max="2" step="0.1" /></label>
        <label class="form-field"><span>最大输出 Token</span><input v-model.number="draft.maxOutputTokens" type="number" min="1" max="32768" /></label>
      </div>

      <div class="capability-grid">
        <label class="capability-toggle"><input v-model="draft.textEnabled" type="checkbox" /><span><Check :size="16" /></span><div><strong>文字问答</strong><small>{{ draft.textModel || "未配置模型" }}</small></div></label>
        <label class="capability-toggle"><input v-model="draft.ttsEnabled" type="checkbox" /><span><Check :size="16" /></span><div><strong>语音合成</strong><input v-model="draft.ttsSpeaker" placeholder="speaker ID" /></div></label>
        <label class="capability-toggle"><input v-model="draft.realtimeEnabled" type="checkbox" /><span><Check :size="16" /></span><div><strong>实时语音</strong><input v-model="draft.realtimeModel" placeholder="realtime model" /></div></label>
        <label class="capability-toggle"><input v-model="draft.deviceControlEnabled" type="checkbox" /><span><Check :size="16" /></span><div><strong>设备工具</strong><small>方舟 Function Calling</small></div></label>
        <label class="capability-toggle"><input v-model="draft.knowledgeEnabled" type="checkbox" /><span><Check :size="16" /></span><div><strong>知识库</strong><small>文档检索增强问答</small></div></label>
      </div>

      <div v-if="draft.realtimeEnabled" class="agent-form-grid realtime-fields">
        <label class="form-field"><span>实时音色</span><input v-model="draft.realtimeSpeaker" /></label>
        <label class="form-field"><span>机器人名称</span><input v-model="draft.realtimeBotName" /></label>
        <label class="form-field span-2"><span>说话风格</span><input v-model="draft.realtimeSpeakingStyle" /></label>
      </div>

      <div v-if="draft.deviceControlEnabled" class="agent-form-grid realtime-fields">
        <label class="form-field span-2">
          <span>允许控制的字段</span>
          <input :value="allowedFieldsText()" placeholder="switch,temperature" @input="updateAllowedFields(($event.target as HTMLInputElement).value)" />
          <small><Cpu :size="12" />使用英文逗号分隔。模型只能调用这里列出的属性。</small>
        </label>
        <div class="form-field span-2">
          <span>允许执行的自定义动作</span>
          <div class="action-access-list">
            <label v-for="action in deviceActions" :key="action.id" class="access-row">
              <input v-model="draft.deviceActionIds" type="checkbox" :value="action.id" />
              <span>{{ action.name }}</span>
              <small>{{ action.device?.name || action.device?.deviceCode || "设备" }} · {{ action.actionKey }}</small>
            </label>
            <div v-if="!deviceActions.length" class="empty-state compact-empty">请先在设备控制中创建自定义动作</div>
          </div>
        </div>
      </div>

      <div v-if="draft.knowledgeEnabled" class="agent-form-grid realtime-fields">
        <label class="capability-toggle span-2">
          <input v-model="draft.knowledgeFallbackToModel" type="checkbox" />
          <span><Check :size="16" /></span>
          <div><strong>知识库未命中时使用通用模型</strong><small>先检索关联知识库，没有匹配资料时再由文字模型回答</small></div>
        </label>
        <label class="form-field span-2">
          <span>{{ draft.knowledgeFallbackToModel ? "通用模型不可用时回复" : "无匹配资料时回复" }}</span>
          <input v-model="draft.knowledgeNoMatchReply" maxlength="500" />
        </label>
        <div class="form-field span-2">
          <span>关联知识库</span>
          <div class="action-access-list">
            <label v-for="base in knowledgeBases" :key="base.id" class="access-row">
              <input v-model="draft.knowledgeBaseIds" type="checkbox" :value="base.id" />
              <span>{{ base.name }}</span>
              <small><BookOpenText :size="12" />{{ base.documents.length }} 份文档 · {{ base.retrievalMode }}</small>
            </label>
            <div v-if="!knowledgeBases.length" class="empty-state compact-empty">请先在知识库页面创建并导入文档</div>
          </div>
        </div>
      </div>

      <div class="access-section">
        <div class="section-header slim"><div><span class="eyebrow">授权</span><h3>客户端访问</h3></div><ShieldCheck :size="18" /></div>
        <label v-for="client in clients" :key="client.id" class="access-row">
          <input type="checkbox" :checked="selectedId ? client.agentIds.includes(selectedId) : false" :disabled="!selectedId" @change="toggleClientAccess(client, ($event.target as HTMLInputElement).checked)" />
          <span>{{ client.name }}</span><small>{{ client.platform }} · {{ client.keyPrefix }}</small>
        </label>
      </div>
      <p v-if="success" class="form-success"><Check :size="16" />{{ success }}</p>
      <p v-if="error" class="form-error">{{ error }}</p>
    </section>
  </div>
</template>
